package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.ThemeAccent
import com.evoapps.evoshift.domain.model.ThemeBase
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.settings.ThemeSettings
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * SH-196: Profile → Appearance: the base (Granat, Butelkowa zieleń, Bordo) and the accent (Złoto,
 * Brąz, Srebro). The dialog previews a combination ([preview]) before it is applied; applying
 * shows it at once and keeps it on this device, then saves it on the profile, and rolls back if
 * that save fails.
 */
class AppearanceViewModel(
    private val settings: ThemeSettings,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val users: UserRepository
) : ViewModel() {

    data class UiState(
        /** The appearance on show. */
        val applied: Appearance,
        /** What the dialog shows (and applies on "Apply"). */
        val preview: Appearance,
        val error: UiText? = null
    )

    private val _state = MutableStateFlow(UiState(settings.current, settings.current))
    val state: StateFlow<UiState> = _state.asStateFlow()

    private var uid: String? = null

    init {
        viewModelScope.launch {
            getCurrentUser().collect { user -> uid = user?.uid }
        }
        // Also a pick taken over from the profile (ProfileAppearanceSync) or rolled back.
        viewModelScope.launch {
            settings.appearance.collect { shown ->
                _state.update { it.copy(applied = shown) }
            }
        }
    }

    val bases: List<ThemeBase> = ThemeBase.entries
    val accents: List<ThemeAccent> = ThemeAccent.entries

    /** The dialog opens on what is shown. */
    fun startPreview() = _state.update { it.copy(preview = settings.current) }

    fun previewBase(base: ThemeBase) = _state.update { it.copy(preview = it.preview.copy(base = base)) }

    fun previewAccent(accent: ThemeAccent) = _state.update { it.copy(preview = it.preview.copy(accent = accent)) }

    /** Applies the previewed combination: at once on screen and on this device, then on the profile. */
    fun apply() {
        val picked = _state.value.preview
        val before = settings.current
        if (picked == before) return
        settings.choose(picked)
        _state.update { it.copy(applied = picked, error = null) }
        val uid = uid ?: return
        viewModelScope.launch {
            users.updateAppearance(uid, picked).onFailure { e ->
                // Rolled back only if nothing newer was picked meanwhile.
                if (settings.current == picked) settings.choose(before)
                _state.update { it.copy(applied = settings.current, error = e.toUiText()) }
            }
        }
    }

    fun messageShown() = _state.update { it.copy(error = null) }
}
